if (!window.HTMLDetailsElement) {
  window.HTMLDetailsElement = HTMLUnknownElement as any;
}

if (!window.HTMLDetailsElement) {
  interface HTMLDetailsElement extends HTMLElement {
    open: boolean;
  }

  const DETAILS = "details";
  const SUMMARY = "summary";

  /**
   * Checks for native browser support for `<details>` element
   * @returns Whether the browser natively supports details/summary
   */
  const checkSupport = (): boolean => {
    const el = document.createElement(DETAILS) as HTMLDetailsElement;
    if (!("open" in el)) return false;

    el.innerHTML = `<${SUMMARY}>a</${SUMMARY}>b`;
    document.body?.appendChild(el);

    const diff = el.offsetHeight;
    el.open = true;
    const result = diff != el.offsetHeight;

    document.body?.removeChild(el);
    return result;
  };

  if (!checkSupport()) {
    const proto = HTMLUnknownElement.prototype;

    Object.defineProperty(proto, "open", {
      get(this: HTMLElement): boolean {
        return this.hasAttribute("open");
      },
      set(this: HTMLElement, value: boolean): void {
        if (this.tagName.toLowerCase() !== DETAILS) return;

        if (value) {
          this.setAttribute("open", "open");
        } else {
          this.removeAttribute("open");
        }

        toggleDetailsChildren(this as HTMLDetailsElement, value);
      },
    });

    /**
     * Toggle visibility of non-summary children in details element
     */
    const toggleDetailsChildren = (details: HTMLDetailsElement, isOpen: boolean): void => {
      const children = details.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        if (child.tagName.toLowerCase() !== SUMMARY) {
          child.style.display = isOpen ? "" : "none";
        }
      }
    };

    /**
     * Click handler for `<summary>` tags
     */
    const clickHandler = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      if (target.nodeName.toLowerCase() === SUMMARY) {
        const details = target.parentNode as HTMLDetailsElement;
        if (!details || details.tagName.toLowerCase() !== DETAILS) return;

        details.open = !details.open;

        e.preventDefault();
      }
    };

    document.addEventListener("click", clickHandler, true);

    const css = `${DETAILS} { display: block; }
    ${DETAILS}:not([open]) > :not(${SUMMARY}) { display: none; }
    ${DETAILS} > ${SUMMARY} { display: block; cursor: pointer; }
    ${DETAILS} > ${SUMMARY}:before { content: "\\u25b6"; display: inline-block; font-size: .8em; width: 1.5em; }
    ${DETAILS}[open] > ${SUMMARY}:before { content: "\\u25bc"; }`;

    document.head.insertAdjacentHTML("afterbegin", "<style>" + css + "</style>");

    const initExistingElements = (): void => {
      const allDetails = document.querySelectorAll(DETAILS);
      for (let i = 0; i < allDetails.length; i++) {
        const details = allDetails[i] as HTMLDetailsElement;
        if (!details.open) {
          toggleDetailsChildren(details, false);
        }
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initExistingElements);
    } else {
      initExistingElements();
    }
  }
}
